import { useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import useDataLayer from "@hooks/useDataLayer";

import { SIGNUP } from "@store/user/userActions";
import { GA4Events } from "@types-runway/analytics";
import { ErrorEvents } from "@types-runway/state";
import { toastError } from "@utils/utils";

import { userUpdate } from "@store/user/userSlice";
import { registration, updateRegistrationObj } from "@utils/saveUserInfo";
import {
  validateFullname,
  validateEmail,
  validateName,
  validatePassword,
  validatePhone,
} from "@utils/forms/utilsFormvalidation";
import { formatPhoneNumberKeepingCaret } from "@utils/forms/formatPhoneNumber";
import { selectHasQuestionnaireAnswers } from "@store/questionnaire/selectHasQuestionnaireAnswers";

import Header from "@elements/Header";
import { QuestionnaireProgress } from "@elements/QuestionnaireProgress";
import DocsModal from "@elements/DocsModal";

import "./SignupPage.scss";
import useOutsideClick from "@hooks/useOutsideClick";
import Checkbox from "@material-ui/core/Checkbox";
import CircleChecked from "@material-ui/icons/CheckCircleOutline";
import CircleUnchecked from "@material-ui/icons/RadioButtonUnchecked";
import InfoPasswordGrey from "./img/InfoPasswordGrey.svg";
import InfoPasswordBlue from "./img/InfoPasswordBlue.svg";
import InfoPasswordRed from "./img/InfoPasswordRed.svg";

import {
  progressSections,
  progressSectionNames,
} from "@utils/questionnaire/progress/progressSections";
import { useTitle } from "@hooks/useTitle";
import {
  Link,
  Text,
  Button,
  FormGroup,
  Label,
  TextField,
} from "@runwayhealth/runway-components-react";

const finishedSegments = progressSections.map((section) => ({
  section,
  name: progressSectionNames[section],
  fillRatio: 1,
  isCurrent: false,
}));

const questionnaireCopy = {
  heading: "Just a few details, then it goes to your clinician",
  submitLabel: "Continue to payment",
};

const plainCopy = {
  kicker: "Online travel health visit",
  heading: "Start your travel health visit",
  supportingLine:
    "Next: an online questionnaire about your trip. A US-licensed clinician reviews it, usually within 24 hours.",
  submitLabel: "Start my visit",
};

const phoneErrorText = "Please enter your phone number";

const SignupPage = () => {
  const dispatch = useDispatch();
  const ref = useRef();
  const emailRef = useRef();
  const checkboxes = [
    {
      label: "I agree to",
      title: "Terms and Conditions",
      subTitle: "Privacy Policy",
      subTitle2: "Information Sharing",
      item: "terms",
      subitem: "privacy",
      subitem2: "information",
    },
    {
      label: "I agree to",
      title: "Telehealth Consent",
      item: "consent",
    },
  ];

  const user = useSelector((state) => state.user);
  const isFromQuestionnaire = useSelector((state) =>
    selectHasQuestionnaireAnswers(state.questions)
  );
  const copy = isFromQuestionnaire ? questionnaireCopy : plainCopy;
  const { reportAccess } = useDataLayer(GA4Events.SIGN_UP);
  const [errorText, setErrorText] = useState(false);
  const [errorPassword, setErrorPassword] = useState(false);
  const [checkedState, setCheckedState] = useState(new Array(checkboxes.length).fill(false));
  const [openDocs, setOpenDocs] = useState(false);
  const [openFormHelp, setOpenFormHelp] = useState(false);
  const [isEmailEditable, setIsEmailEditable] = useState(false);
  const [docName, setDocName] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const isEmailLocked = isFromQuestionnaire && !isEmailEditable;
  const handleOnChange = (position) => {
    const updatedCheckedState = checkedState.map((item, index) =>
      index === position ? !item : item
    );

    setCheckedState(updatedCheckedState);
  };
  useTitle("Sign Up");

  useEffect(() => {
    if (isEmailEditable) emailRef.current.focus();
  }, [isEmailEditable]);
  const history = useHistory();

  useEffect(() => {
    if (user.error.event === ErrorEvents.SERVER_ERROR) {
      setErrorText(user.error.message || "Unknown error.");
      toastError("Error", user.error.message);
    }
  }, [user.error]);

  useOutsideClick(ref, () => {
    if (openFormHelp) setOpenFormHelp(false);
  });

  const validateInput = (value, name) => {
    setErrorPassword("");
    setErrorText("");

    if (name === "password" && !validatePassword(value)) {
      setErrorPassword("Password must be at least 8 characters long.");
    }

    if (name === "first_name" && !validateName(value)) {
      setErrorText("Please enter a valid Name");
    }

    if (name === "last_name" && !validateName(value)) {
      setErrorText("Please enter a valid Last Name");
    }

    if (name === "phone" && !validatePhone(value)) {
      setErrorText(phoneErrorText);
    }

    if (name === "email" && !validateEmail(value)) {
      setErrorText("Please enter your email address");
    }
  };

  const formIsValid = () => {
    if (!validateFullname(registration.first_name, registration.last_name)) {
      setErrorText("Please enter your full name");
      return false;
    }
    if (!validateEmail(registration.email ?? user.data.email)) {
      setErrorText("Please enter your email address");
      return false;
    }
    if (!validatePassword(registration.password)) {
      setErrorPassword("Please enter a more secure password.");
      return false;
    }
    if (!validatePhone(registration.phone)) {
      setErrorText(phoneErrorText);
      return false;
    }
    if (checkedState.includes(false)) {
      setErrorText("Please accept our terms and conditions");
      return false;
    }
    return true;
  };

  const submitForm = () => {
    if (formIsValid()) {
      // Submit signup data.
      // Report signup analytics
      reportAccess();
      dispatch({
        type: SIGNUP,
        user: user.data,
      });
    }
  };
  const handleOpener = (value) => {
    setDocName(value);
    setOpenDocs(true);
  };

  return (
    <div className="sign-up-page">
      {isFromQuestionnaire ? (
        <Header
          progressIndicator={
            <QuestionnaireProgress segments={finishedSegments} subheader="Last step" />
          }
          previous={() => history.push("/consultation")}
        />
      ) : (
        <Header hasProgressBar={false} previous={() => history.push("/consultation")} />
      )}
      <div className="form-wrapper">
        <div className="sign-container">
          <div className="sign-up-content">
            <div className="signup-form">
              {!isFromQuestionnaire && <p className="sign-up-kicker">{plainCopy.kicker}</p>}
              <h1 className="sign-up">{copy.heading}</h1>
              {!isFromQuestionnaire && (
                <p className="sign-up-supporting-line">{plainCopy.supportingLine}</p>
              )}
              <div className="form">
                <div className="input-names">
                  <FormGroup>
                    <Label htmlFor="first-name">First name</Label>
                    <TextField
                      data-testid="signup-first-name"
                      autoComplete="given-name"
                      placeholder="First Name"
                      id="first-name"
                      onChange={(e) => {
                        updateRegistrationObj("first_name", e.target.value);
                        dispatch(
                          userUpdate({
                            name: "first_name",
                            value: e.target.value,
                          })
                        );
                      }}
                      onBlur={(e) => {
                        validateInput(e.target.value, "first_name");
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="last-name">Last name</Label>
                    <TextField
                      data-testid="signup-last-name"
                      placeholder="Last Name"
                      autoComplete="family-name"
                      id="last-name"
                      onChange={(e) => {
                        updateRegistrationObj("last_name", e.target.value);
                        dispatch(
                          userUpdate({
                            name: "last_name",
                            value: e.target.value,
                          })
                        );
                      }}
                      onBlur={(e) => {
                        validateInput(e.target.value, "last_name");
                      }}
                    />
                  </FormGroup>
                </div>
                <FormGroup>
                  <Label htmlFor="email">Email address</Label>
                  <div className="email-field">
                    <TextField
                      ref={emailRef}
                      data-testid="signup-email"
                      placeholder="Email Address"
                      id="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      readOnly={isEmailLocked}
                      value={user.data.email ?? ""}
                      onChange={(e) => {
                        updateRegistrationObj("email", e.target.value);
                        dispatch(userUpdate({ name: "email", value: e.target.value }));
                      }}
                      onBlur={(e) => validateInput(e.target.value, "email")}
                    />
                    {isEmailLocked && (
                      <button
                        type="button"
                        className="email-edit-button"
                        aria-label="Edit email"
                        data-testid="signup-email-edit"
                        onClick={() => setIsEmailEditable(true)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="phone">Phone number</Label>
                  <TextField
                    data-testid="signup-phone"
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="Phone Number"
                    value={phoneValue}
                    onChange={(e) => {
                      const phoneInput = e.target;
                      const { value: formattedPhone, caretPosition } =
                        formatPhoneNumberKeepingCaret(phoneInput.value, phoneInput.selectionStart);
                      // React restores the controlled value after this handler; place the caret afterwards.
                      queueMicrotask(() =>
                        phoneInput.setSelectionRange(caretPosition, caretPosition)
                      );
                      setPhoneValue(formattedPhone);
                      updateRegistrationObj("phone", formattedPhone);
                      if (errorText === phoneErrorText) validateInput(formattedPhone, "phone");
                      dispatch(
                        userUpdate({
                          name: "phone_number",
                          value: formattedPhone,
                        })
                      );
                      dispatch(
                        userUpdate({
                          name: "phone_type",
                          value: 2,
                        })
                      );
                    }}
                    onBlur={(e) => validateInput(e.target.value, "phone")}
                  />
                  {isFromQuestionnaire && (
                    <p className="signup-helper">
                      Your clinician may text you about your prescription.
                    </p>
                  )}
                </FormGroup>
                <FormGroup className="password-signup-footer">
                  <Label htmlFor="password">Create password</Label>
                  <div ref={ref} className="signup-password-field">
                    <div
                      className={
                        openFormHelp
                          ? errorPassword
                            ? "container-helpform active errorExist"
                            : "container-helpform active"
                          : "container-helpform"
                      }
                    >
                      <span className={errorPassword ? "top-bot errorExist" : "top-bot"}></span>
                      <div className="container-text">
                        <p>Your password must:</p>
                        <ul className="list-items">
                          <li>contain at least 8 characters</li>
                          {errorPassword ? <li>be different from the previous password</li> : null}
                        </ul>
                      </div>
                    </div>
                    <div className="signup-password-input">
                      <TextField
                        data-testid="signup-password"
                        id="password"
                        autoComplete="new-password"
                        type="password"
                        placeholder="Create Password"
                        isInvalid={Boolean(errorPassword)}
                        value={passwordValue}
                        onChange={(e) => {
                          setPasswordValue(e.target.value);
                          updateRegistrationObj("password", e.target.value);
                          if (errorPassword) validateInput(e.target.value, "password");
                          dispatch(
                            userUpdate({
                              name: "password",
                              value: e.target.value,
                            })
                          );
                        }}
                        onBlur={(e) => validateInput(e.target.value, "password")}
                      />
                    </div>
                    <div className={"img-container"}>
                      {errorPassword && (
                        <img
                          className="imgItemInfo"
                          onClick={() => setOpenFormHelp(!openFormHelp)}
                          src={InfoPasswordRed}
                          alt=""
                        />
                      )}
                      {!errorPassword && (
                        <img
                          className="imgItemInfo"
                          onClick={() => setOpenFormHelp(!openFormHelp)}
                          src={openFormHelp ? InfoPasswordBlue : InfoPasswordGrey}
                          alt=""
                        />
                      )}
                    </div>
                  </div>
                  <div
                    className={
                      errorPassword || errorText
                        ? "error-message-signup active"
                        : "error-message-signup"
                    }
                  >
                    <p className="message-content-signup">{errorPassword || errorText}</p>
                  </div>
                </FormGroup>

                <div className="agreement">
                  {checkboxes.map((checkbox, index) => (
                    <div key={index} className="terms">
                      <Checkbox
                        data-testid={`signup-checkbox-${index}`}
                        icon={<CircleUnchecked />}
                        checkedIcon={<CircleChecked />}
                        checked={checkedState[index]}
                        onClick={() => setErrorText(false)}
                        onChange={() => handleOnChange(index)}
                        color="default"
                      />
                      <Text as="div">
                        {checkbox.label}{" "}
                        <Link as="span" onClick={() => handleOpener(checkbox.item)}>
                          {checkbox.title}
                        </Link>
                        {checkbox.subTitle && ", "}
                        <Link as="span" onClick={() => handleOpener(checkbox.subitem)}>
                          {checkbox?.subTitle}
                        </Link>
                        {checkbox.subTitle2 && " and "}
                        <Link as="span" onClick={() => handleOpener(checkbox.subitem2)}>
                          {checkbox?.subTitle2}
                        </Link>
                      </Text>
                    </div>
                  ))}
                </div>

                <div className="button-div">
                  <Button
                    data-testid="signup-button"
                    id="signup-btn"
                    size="lg"
                    customWidth={440}
                    iconName="ArrowRight"
                    isLoading={user.isPending}
                    onClick={() => submitForm()}
                  >
                    {copy.submitLabel}
                  </Button>
                  {openDocs && <DocsModal open={openDocs} close={setOpenDocs} name={docName} />}
                </div>
                <div className="login-link-container">
                  <Text>
                    Already a member?{" "}
                    <Link
                      as="span"
                      onClick={() => history.push("/login", { from: "registration" })}
                    >
                      Log in
                    </Link>
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
